// oxlint-disable-next-line no-restricted-imports
import { useAuth0 } from '@auth0/auth0-react';
import { TransportProvider } from '@connectrpc/connect-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { type RegisteredRouter, RouterProvider, useRouterState } from '@tanstack/react-router';
import { SnackbarProvider } from 'notistack';
import { memo, useState } from 'react';
import { createQueryClient } from '@/api/query-client';
import { transport } from '@/api/transport';
import { NetworkStatus } from '@/components/Common/NetworkStatus';
import { VersionAlert } from '@/components/Common/VersionAlert';
import { type AuthContext } from '@/helpers/auth';
import { LoadingIndicator } from './Common/LoadingIndicator';

export const AppRoot = memo(function AppRoot({ router }: { router: RegisteredRouter }) {
  const { isLoading, ...auth } = useAuth0();
  // One splash from auth start-up until the first route resolves: kept at the same place in the tree across both
  // phases, so a refresh shows it once instead of twice. The router has no resolved location until then.
  const isFirstLoad = useRouterState({ router, select: (state) => state.resolvedLocation === undefined });

  return (
    <>
      {isLoading ? null : <AppRootInner router={router} auth={auth} />}
      {isFirstLoad && <LoadingIndicator />}
    </>
  );
});

function AppRootInner({ router, auth }: { router: RegisteredRouter; auth: AuthContext }) {
  const [context] = useState(() => ({ queryClient: createQueryClient(router, auth), transport, auth }));

  return (
    <QueryClientProvider client={context.queryClient}>
      <TransportProvider transport={context.transport}>
        <SnackbarProvider maxSnack={3}>
          <VersionAlert />
          <NetworkStatus />
          <RouterProvider router={router} context={context} />
        </SnackbarProvider>
      </TransportProvider>
    </QueryClientProvider>
  );
}
